import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useApiClient } from "../../providers/AuthProvider";
import { useTenant } from "../../providers/TenantProvider";

// --- Types ---

export type MediaFile = {
  id: string;
  tenantId: string;
  folderId: string;
  blobName: string;
  originalFilename: string;
  contentType: string;
  sizeBytes: number;
  uploadedBy: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  downloadUrl: string;
  tags: string[];
  canEdit: boolean;
};

export type MediaFolder = {
  id: string;
  tenantId: string;
  locationGroupId: string;
  parentId: string | null;
  name: string;
  descendantsCanView: boolean;
  descendantsCanEdit: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  fileCount: number;
  subfolderCount: number;
  canView: boolean;
  canEdit: boolean;
  canCreateChild: boolean;
  canUpload: boolean;
  isPathOnly: boolean;
};

export type CreateFolderInput = {
  name: string;
  locationGroupId?: string;
  descendantsCanView?: boolean;
  descendantsCanEdit?: boolean;
};

type MediaContextType = {
  // Data
  files: MediaFile[];
  folders: MediaFolder[];
  loading: boolean;
  uploading: boolean;
  error: string | null;

  // Navigation
  currentFolderId: string | null;
  breadcrumbs: MediaFolder[];
  navigateToFolder: (folderId: string | null) => void;

  // Search
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  isSearching: boolean;

  // Selection (file and folder are mutually exclusive)
  selectedFileId: string | null;
  selectFile: (id: string | null) => void;
  selectedFolderId: string | null;
  selectFolder: (id: string | null) => void;

  // CRUD operations
  fetchFiles: () => Promise<void>;
  fetchFolders: () => Promise<void>;
  uploadFile: (file: File) => Promise<MediaFile>;
  deleteFile: (id: string) => Promise<void>;
  updateFile: (id: string, data: { tags?: string[]; metadata?: Record<string, unknown> }) => Promise<void>;

  // Current folder (parent of children, target for uploads/new folders)
  currentFolder: MediaFolder | null;

  // Folder operations
  createFolder: (input: CreateFolderInput) => Promise<MediaFolder>;
  deleteFolder: (id: string) => Promise<void>;
  renameFolder: (id: string, name: string) => Promise<void>;
  updateFolder: (
    id: string,
    input: { name?: string; locationGroupId?: string; descendantsCanView?: boolean; descendantsCanEdit?: boolean },
  ) => Promise<MediaFolder>;
};

const MediaContext = createContext<MediaContextType | null>(null);

// --- Provider ---

type Props = {
  children: React.ReactNode;
};

export const MediaProvider = ({ children }: Props) => {
  const apiClient = useApiClient();
  const { currentTenantId } = useTenant();

  // Data state
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [folders, setFolders] = useState<MediaFolder[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Navigation state
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<MediaFolder[]>([]);
  const [currentFolder, setCurrentFolder] = useState<MediaFolder | null>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  // Selection state (mutually exclusive)
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);

  // --- Fetch files ---

  const fetchFiles = useCallback(async () => {
    if (!currentTenantId) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("tenantId", currentTenantId);

      if (searchQuery) {
        params.set("search", searchQuery);
        setIsSearching(true);
      } else {
        params.set("folderId", currentFolderId ?? "null");
        setIsSearching(false);
      }

      const data = await apiClient.get<{ files: MediaFile[] }>(`/api/media/files?${params}`);
      setFiles(data.files);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiClient, currentFolderId, searchQuery, currentTenantId]);

  // --- Fetch folders ---

  const fetchFolders = useCallback(async () => {
    if (!currentTenantId) return;
    try {
      const params = new URLSearchParams();
      params.set("tenantId", currentTenantId);
      params.set("parentId", currentFolderId ?? "null");
      const data = await apiClient.get<{ folders: MediaFolder[] }>(`/api/media/folders?${params}`);
      setFolders(data.folders);
    } catch (err: any) {
      console.error("Failed to fetch folders:", err);
    }
  }, [apiClient, currentFolderId, currentTenantId]);

  // --- Fetch breadcrumbs ---

  const fetchBreadcrumbs = useCallback(async () => {
    if (!currentFolderId) {
      setBreadcrumbs([]);
      return;
    }
    try {
      const data = await apiClient.get<{ path: MediaFolder[] }>(`/api/media/folders/${currentFolderId}/path`);
      setBreadcrumbs(data.path);
    } catch (err: any) {
      console.error("Failed to fetch breadcrumbs:", err);
      setBreadcrumbs([]);
    }
  }, [apiClient, currentFolderId]);

  const fetchCurrentFolder = useCallback(async () => {
    if (!currentFolderId) {
      setCurrentFolder(null);
      return;
    }
    try {
      const data = await apiClient.get<{ folder: MediaFolder }>(`/api/media/folders/${currentFolderId}`);
      setCurrentFolder(data.folder);
    } catch (err: any) {
      console.error("Failed to fetch current folder:", err);
      setCurrentFolder(null);
    }
  }, [apiClient, currentFolderId]);

  // --- Navigation ---

  const navigateToFolder = useCallback((folderId: string | null) => {
    setCurrentFolderId(folderId);
    setSelectedFileId(null);
    setSelectedFolderId(null);
    setSearchQuery("");
  }, []);

  // --- Selection ---

  const selectFile = useCallback((id: string | null) => {
    setSelectedFileId(id);
    if (id) setSelectedFolderId(null);
  }, []);

  const selectFolder = useCallback((id: string | null) => {
    setSelectedFolderId(id);
    if (id) setSelectedFileId(null);
  }, []);

  // --- Upload file ---

  const getVideoDuration = (file: File): Promise<number | null> => {
    return new Promise((resolve) => {
      if (!file.type.startsWith("video/")) {
        resolve(null);
        return;
      }
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => {
        const duration = video.duration;
        URL.revokeObjectURL(video.src);
        // Default to 15 seconds if duration can't be determined
        resolve(isFinite(duration) ? duration : 15);
      };
      video.onerror = () => {
        URL.revokeObjectURL(video.src);
        // Default to 15 seconds on error
        resolve(15);
      };
      video.src = URL.createObjectURL(file);
    });
  };

  const getImageDimensions = (file: File): Promise<{ width: number; height: number } | null> => {
    return new Promise((resolve) => {
      if (!file.type.startsWith("image/")) {
        resolve(null);
        return;
      }

      const image = new window.Image();
      const objectUrl = URL.createObjectURL(file);
      image.onload = () => {
        resolve({ width: image.naturalWidth, height: image.naturalHeight });
        URL.revokeObjectURL(objectUrl);
      };
      image.onerror = () => {
        resolve(null);
        URL.revokeObjectURL(objectUrl);
      };
      image.src = objectUrl;
    });
  };

  const getVideoDimensions = (file: File): Promise<{ width: number; height: number } | null> => {
    return new Promise((resolve) => {
      if (!file.type.startsWith("video/")) {
        resolve(null);
        return;
      }

      const video = document.createElement("video");
      const objectUrl = URL.createObjectURL(file);
      video.preload = "metadata";
      video.onloadedmetadata = () => {
        resolve({ width: video.videoWidth, height: video.videoHeight });
        URL.revokeObjectURL(objectUrl);
      };
      video.onerror = () => {
        resolve(null);
        URL.revokeObjectURL(objectUrl);
      };
      video.src = objectUrl;
    });
  };

  const uploadFile = useCallback(
    async (file: File): Promise<MediaFile> => {
      const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500 MB
      if (file.size > MAX_FILE_SIZE) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(0);
        throw new Error(`File is too large (${sizeMB} MB). Maximum allowed size is 500 MB.`);
      }
      if (!currentFolderId) {
        throw new Error("Open a folder before uploading files.");
      }

      setUploading(true);
      setError(null);
      try {
        const [duration, imageDimensions, videoDimensions] = await Promise.all([
          getVideoDuration(file),
          getImageDimensions(file),
          getVideoDimensions(file),
        ]);
        const dimensions = imageDimensions ?? videoDimensions;

        const formData = new FormData();
        formData.append("file", file);
        formData.append("folderId", currentFolderId);
        const metadata: Record<string, unknown> = {};
        if (duration !== null) {
          metadata.duration = duration;
        }
        if (dimensions) {
          metadata.width = dimensions.width;
          metadata.height = dimensions.height;
        }
        if (Object.keys(metadata).length > 0) {
          formData.append("metadata", JSON.stringify(metadata));
        }

        const response = await fetch("/api/media/files", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
          },
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Upload failed");
        }

        const data = await response.json();
        const fileWithTags = { ...data.file, tags: data.file.tags ?? [] };
        setFiles((prev) => [fileWithTags, ...prev]);
        return fileWithTags;
      } catch (err: any) {
        setError(err.message);
        throw err;
      } finally {
        setUploading(false);
      }
    },
    [currentFolderId, currentTenantId],
  );

  // --- Delete file ---

  const deleteFile = useCallback(
    async (id: string): Promise<void> => {
      await apiClient.delete(`/api/media/files/${id}`);
      setFiles((prev) => prev.filter((f) => f.id !== id));
      setSelectedFileId((current) => (current === id ? null : current));
    },
    [apiClient],
  );

  // --- Update file ---

  const updateFile = useCallback(
    async (id: string, data: { tags?: string[]; metadata?: Record<string, unknown> }): Promise<void> => {
      const result = await apiClient.put<{ file: MediaFile }>(`/api/media/files/${id}`, data);
      setFiles((prev) => prev.map((f) => (f.id === id ? result.file : f)));
    },
    [apiClient],
  );

  // --- Folder operations ---

  const createFolder = useCallback(
    async (input: CreateFolderInput): Promise<MediaFolder> => {
      const data = await apiClient.post<{ folder: MediaFolder }>("/api/media/folders", {
        name: input.name,
        parentId: currentFolderId,
        locationGroupId: input.locationGroupId ?? (currentFolderId ? undefined : currentTenantId),
        descendantsCanView: input.descendantsCanView,
        descendantsCanEdit: input.descendantsCanEdit,
      });
      setFolders((prev) => [...prev, data.folder]);
      return data.folder;
    },
    [apiClient, currentFolderId, currentTenantId],
  );

  const deleteFolder = useCallback(
    async (id: string): Promise<void> => {
      await apiClient.delete(`/api/media/folders/${id}`);
      setFolders((prev) => prev.filter((f) => f.id !== id));
    },
    [apiClient],
  );

  const renameFolder = useCallback(
    async (id: string, name: string): Promise<void> => {
      const data = await apiClient.put<{ folder: MediaFolder }>(`/api/media/folders/${id}`, { name });
      setFolders((prev) => prev.map((f) => (f.id === id ? { ...f, ...data.folder } : f)));
    },
    [apiClient],
  );

  const updateFolder = useCallback(
    async (
      id: string,
      input: { name?: string; locationGroupId?: string; descendantsCanView?: boolean; descendantsCanEdit?: boolean },
    ): Promise<MediaFolder> => {
      const data = await apiClient.put<{ folder: MediaFolder }>(`/api/media/folders/${id}`, input);
      setFolders((prev) => prev.map((f) => (f.id === id ? { ...f, ...data.folder } : f)));
      setCurrentFolder((current) => (current?.id === id ? { ...current, ...data.folder } : current));
      return data.folder;
    },
    [apiClient],
  );

  // --- Load data on mount and when folder/search changes ---

  useEffect(() => {
    fetchFiles();
    fetchFolders();
    fetchBreadcrumbs();
    fetchCurrentFolder();
  }, [fetchFiles, fetchFolders, fetchBreadcrumbs, fetchCurrentFolder]);

  // --- Context value ---

  const value: MediaContextType = {
    files,
    folders,
    loading,
    uploading,
    error,
    currentFolderId,
    currentFolder,
    breadcrumbs,
    navigateToFolder,
    searchQuery,
    setSearchQuery,
    isSearching,
    selectedFileId,
    selectFile,
    selectedFolderId,
    selectFolder,
    fetchFiles,
    fetchFolders,
    uploadFile,
    deleteFile,
    updateFile,
    createFolder,
    deleteFolder,
    renameFolder,
    updateFolder,
  };

  return <MediaContext.Provider value={value}>{children}</MediaContext.Provider>;
};

// --- Hook ---

export const useMedia = (): MediaContextType => {
  const context = useContext(MediaContext);
  if (!context) {
    throw new Error("useMedia must be used within a MediaProvider");
  }
  return context;
};
